import { log } from '@/log'
import AddItemToCart, { AddItemToCartResult, NewCartItem } from '@/models/cart/command/add-item-to-cart'
import { defaultAddItemToCartErrorMessage } from '@/models/cart/error/error-response-parser'
import ContactLensSKU from '@/models/item/sku/contact-lens'
import { FetchSkuPrice } from '@/models/item/sku/repository/fetch-sku-price'
import { CookieService } from '@/services/cookie'
import { EventBusForThirdParty } from '@/usecases/event-bus-for-third-party/event-bus'
import { BaseError } from '@/util/base-error'
import ContactLensItemDetailStore from '@ajax/vue/components/organisms/contact-lens/store'
import { Either, left, right } from 'fp-ts/lib/Either'
import {
  fromNullable as optionFromNullable,
  none,
  Option,
  some
} from 'fp-ts/lib/Option'
import { flatMap } from 'lodash'

type ErrorMessage = string

export type ContactLensItemDetailStoreForCart = Pick<
  ContactLensItemDetailStore,
  'selectedRightSku' |
  'selectedRightBoxCount' |
  'selectedLeftSku' |
  'selectedLeftBoxCount' |
  'selectedFku'
>
export type FetchSkuPriceForCart = Pick<FetchSkuPrice, 'fetchById'>

export async function addLensToCart(
  store: ContactLensItemDetailStoreForCart,
  fetchSkuPrice: FetchSkuPriceForCart,
  addItemToCart: AddItemToCart
): Promise<Either<ErrorMessage, AddItemToCartResult[]>> {
  const resultsNewCartParams = await buildNewCartParamsFromStore(store, fetchSkuPrice)

  return resultsNewCartParams.fold(
    async (errors): Promise<Either<string, AddItemToCartResult[]>> => {
      log.error({
        context: 'failed-build-new-cart-param',
        errors,
      })

      return left(defaultAddItemToCartErrorMessage)
    },
    (newCartParams) => {
      const items = newCartParams.map(ncp => ({
        productId: ncp.productId,
        price: ncp.price,
        count: ncp.count,
      }))

      try {
        publishAddLensToCartDeveloperReports(store, items)
      } catch (e) {
        log.error(e)
      }

      return addItemToCart.execute(items)
    }
  )
}

/**
 * @internal
 */
function buildNewCartParamsFromStore(
  store: ContactLensItemDetailStoreForCart,
  fetchSkuPrice: FetchSkuPriceForCart
): Promise<Either<Error[], NewCartItem[]>> {
  const buildNewCartParamForSku = setupBuildNewCartParamForSku(fetchSkuPrice)

  return Promise
    .all([
      buildNewCartParamForSku(optionFromNullable(store.selectedRightSku), store.selectedRightBoxCount),
      buildNewCartParamForSku(optionFromNullable(store.selectedLeftSku), store.selectedLeftBoxCount),
    ])
    .then(extractOnlySome)
    .then(eitherListToEither)
    .then(results => results.filterOrElse(
      params => params.length > 0,
      [new AnyNewCartParameterNotExist()]
    ))
}

/**
 * @internal
 * TODO: このメソッドはカート追加以外の処理なので、他のファイルに移動させる
 * @see https://github.com/Everforth/growthpalette-front/pull/7893#discussion_r1493323764
 */
function publishAddLensToCartDeveloperReports(
  store: ContactLensItemDetailStoreForCart,
  items: NewCartItem[]
) {
  /**
   * NOTE: js-publish-add-item-report クラスの構造は変わる可能性があるため、
   *       その要素が持つ必要な情報をカート追加メソッドのパラメータとして受け取るようにした方がよい。
   * @see https://github.com/Everforth/growthpalette-front/pull/7893#discussion_r1493330135
   */
  const dropDownInputs = Array.from(document.querySelectorAll('.js-publish-add-item-report'))
    .map(elem => ({
      display: elem.querySelector('.js-publish-add-item-report__display'),
      select: elem.querySelector('select'),
    }))
    .map(({ display, select }) => ({
      class: display ? display.className : '',
      displayedValue: display ? displayedValueOf(display) : '',
      selected: {
        value: select ? select.value : '', // <select> タグで選択されている値
        label: select ? select.options[select.selectedIndex].label : '', // <select> タグで選択されている値のラベル
      },
    }))

  EventBusForThirdParty.getInstance().publish('publish-developer-reports', [{
    userId: CookieService.getUserId(),
    level: 'info',
    timestamp: new Date(),
    /**
     * ユーザーからのお問い合わせのバグ調査のため、以下をログとして出力する。
     * dropDownInputs: 表示用要素と<select>により画面上で表示されている値と選択されている値
     * selected: JS の内部状態として選択されている状態
     * items: Front のカート追加 API へのリクエストのパラメータ
     * @see https://github.com/Everforth/growthpalette-front/pull/7893#discussion_r1492445572
     */
    message: {
      dropDownInputs,
      selected: {
        selectedFkuId: {
          long: store.selectedFku ? store.selectedFku.id : null,
          short: store.selectedFku ? store.selectedFku.shortId : null,
        },
        selectedColor: store.selectedFku ? store.selectedFku.colorLabel : null,
        selectedLeftSkuId: store.selectedLeftSku ? store.selectedLeftSku.id : null,
        selectedLeftPower: store.selectedLeftSku ? store.selectedLeftSku.power : null,
        selectedLeftBoxCount: store.selectedLeftBoxCount,
        selectedRightSkuId: store.selectedRightSku ? store.selectedRightSku.id : null,
        selectedRightPower: store.selectedRightSku ? store.selectedRightSku.power : null,
        selectedRightBoxCount: store.selectedRightBoxCount,
      },
      items,
      userId: CookieService.getUserId(),
    },
    reportSource: 'addItemToCart',
  }])
}

// 表示用要素はカラー・数量が input、度数が div のため、値の取り方が要素の形式で異なる
function displayedValueOf(display: Element): string {
  return display instanceof HTMLInputElement ? display.value : (display.textContent || '').trim()
}

export class AnyNewCartParameterNotExist extends BaseError {
  constructor(message: string = 'any new-cart-parameter not exist') {
    super(message)
  }
}

function setupBuildNewCartParamForSku(
  fetchSkuPrice: FetchSkuPriceForCart
) {
  return async (
    maybeSku: Option<ContactLensSKU>,
    boxCount: number
  ): Promise<Option<Either<Error, NewCartItem>>> => maybeSku.fold(
    Promise.resolve(none),
    async (sku): Promise<Option<Either<Error, NewCartItem>>> => {
      const maybePrice = await fetchSkuPrice.fetchById(sku.id.long)

      return some(maybePrice.map(price => ({
        productId: sku.id.long,
        count: boxCount,
        price,
      })))
    }
  )
}

function extractOnlySome<T>(xs: Array<Option<T>>): T[] {
  return flatMap(xs, x => x.fold([], v => [v]))
}
function eitherListToEither<L, R>(xs: Array<Either<L, R>>): Either<L[], R[]> {
  return xs.reduce(
    (sum: Either<L[], R[]>, x: Either<L, R>): Either<L[], R[]> => sum.fold(
      lefts => left(
        x.fold(l => [...lefts, l], () => lefts)
      ),
      rights => x.fold(
        l => left([l]),
        r => right([...rights, r])
      )
    ),
    right([])
  )
}

/**
 * レンズの度が画面表示とJS内部で一致しているかどうかを判定する
 *
 * @param leftSkuIdFromStore ストアの左目SKU ID（選択なしの場合はnull）
 * @param rightSkuIdFromStore ストアの右目SKU ID（選択なしの場合はnull）
 * @see https://github.com/Everforth/growthpalette-front/issues/8449
 */
export function detectDisplayMismatch(
  leftSkuIdFromStore: string | null,
  rightSkuIdFromStore: string | null
): boolean {
  const selects = Array.from(
    document.querySelectorAll('.powers .js-publish-add-item-report select')
  ) as HTMLSelectElement[]

  // 左目と右目のセレクトボックスがあること
  const EXPECTED_SELECT_COUNT = 2
  if (selects.length < EXPECTED_SELECT_COUNT) {
    return false
  }

  const notSelectedValue = 'not_selected'
  const leftSelectValue = selects[0].value
  const rightSelectValue = selects[1].value

  // 左目の不一致チェック
  const expectedLeftValue = leftSkuIdFromStore || notSelectedValue
  if (leftSelectValue !== expectedLeftValue) {
    return true
  }

  // 右目の不一致チェック
  const expectedRightValue = rightSkuIdFromStore || notSelectedValue
  if (rightSelectValue !== expectedRightValue) {
    return true
  }

  return false
}

/**
 * 度数の画面表示とJS内部状態の不一致を検出した場合にwarnレベルのログをサーバーに送信する
 * autocomplete=off で根本対応済みだが、再発時に気付けるようにするためのもの
 *
 * @see https://github.com/Everforth/growthpalette-front/issues/8449
 */
export function reportDisplayMismatch(
  store: ContactLensItemDetailStoreForCart
): void {
  EventBusForThirdParty.getInstance().publish(
    'publish-developer-reports',
    buildDisplayMismatchReport(store)
  )
}

export function buildDisplayMismatchReport(
  store: ContactLensItemDetailStoreForCart
) {
  return [{
    userId: CookieService.getUserId(),
    level: 'warning' as 'warning',
    timestamp: new Date(),
    message: {
      description: 'contact-lens-power-display-store-mismatch',

      // JSの内部状態が保持するSKU ID
      store: {
        selectedLeftSkuId: store.selectedLeftSku ? store.selectedLeftSku.id : null,
        selectedRightSkuId: store.selectedRightSku ? store.selectedRightSku.id : null,
      },

      // DOMの度数セレクトボックスが保持する値（[0]:左目, [1]:右目）
      dom: {
        selects: Array.from(
          document.querySelectorAll('.powers .js-publish-add-item-report select')
        ).map((s: Element) => (s as HTMLSelectElement).value),
      },

      // ユーザーエージェント
      userAgent: navigator.userAgent,

      // 問題が発生したページのURL
      url: window.location.href,

      // 直前に閲覧していたページのURL
      referrer: document.referrer,

      // セッション中の履歴数
      historyLength: history.length,
    },
    reportSource: 'addItemToCart' as 'addItemToCart',
  }]
}
